import { CheckCircle, Loader2, XCircle, Clock } from 'lucide-react';

export const JOB_TYPE_LABELS = {
  technical: 'Technical',
  'non-technical': 'Non-technical',
  business: 'Business',
};

export const CATEGORY_LABELS_HISTORY = {
  behavioral: 'Behavioral',
  'role-specific': 'Role-specific',
  leadership: 'Leadership',
};

export const CATEGORY_TEXT_COLOR = {
  behavioral: { light: 'text-primary-600', dark: 'text-primary-400' },
  'role-specific': { light: 'text-yellow-600', dark: 'text-yellow-400' },
  leadership: { light: 'text-red-600', dark: 'text-red-400' },
};

export function categoryTextColor(category, isDarkMode) {
  const c = CATEGORY_TEXT_COLOR[category];
  if (!c) return isDarkMode ? 'text-slate-400' : 'text-slate-500';
  return isDarkMode ? c.dark : c.light;
}

// Backend status strings vary — map by substring to the 4 states the UI handles. Every value
// returned here needs a HISTORY_STATUS_CONFIG entry (StatusBadge falls back to 'incomplete' otherwise).
export function normalizeSessionStatus(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('done') || s.includes('finish')) return 'done';
  if (s.includes('transcribing')) return 'transcribing';
  if (s.includes('analyzing') || s.includes('loading')) return 'loading';
  if (s.includes('fail') || s.includes('error')) return 'error';
  return 'incomplete';
}

// Normalized statuses that mean the backend is still working on the session.
export const PROCESSING_STATUSES = ['transcribing', 'loading'];
export const isProcessingStatus = (status) => PROCESSING_STATUSES.includes(status);

export function formatSessionDate(isoString) {
  if (!isoString) return '—';
  return isoString.split('T')[0];
}

export function normalizeHistorySession(row) {
  const overallScore = row.AnalyzedJSON?.overall_feedback?.overall_score;
  return {
    id: row.SessionID,
    role: JOB_TYPE_LABELS[row.JobType] || row.JobType || 'Mock Interview',
    score: Number.isFinite(overallScore) ? Math.round(overallScore) : null,
    date: formatSessionDate(row.StartedAt),
    status: normalizeSessionStatus(row.Status),
    failureReason: row.AnalysisErrorMessage || null,
  };
}

// Interview scores are 0-100 like resume scores, so scoreTone / PILL_* / BAR from resumeReport are
// reused wholesale; only the rating/headline wording here is interview-specific.
export function interviewRating(score100) {
  if (score100 == null) return 'Pending';
  if (score100 >= 80) return 'Strong';
  if (score100 >= 60) return 'Developing';
  return 'Needs Work';
}

export function interviewHeadline(score100) {
  if (score100 == null) return 'Your session is being scored.';
  if (score100 >= 80) return 'Strong performance. Keep sharpening the details.';
  if (score100 >= 60) return 'Solid foundation. A few focused tweaks will lift this.';
  return "Key gaps to work on. Let's close them.";
}

// snake_cased category_breakdown key -> "Problem Solving" etc.
export function prettyCategory(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// -> [{ key, label, score100 }], highest-first — shared by the on-screen report and the PDF.
export function categoryBreakdownRows(overallFeedback) {
  const cb = overallFeedback?.category_breakdown;
  if (!cb || typeof cb !== 'object') return [];
  return Object.entries(cb)
    .map(([key, val]) => ({
      key,
      label: prettyCategory(key),
      score100: Number.isFinite(val?.category_score) ? Math.round(val.category_score) : null,
    }))
    .sort((a, b) => (b.score100 ?? -1) - (a.score100 ?? -1));
}

// STAR is a narrative sequence, not sorted by score like categoryBreakdownRows — the
// AI doesn't reliably return `components` keys in this order, so it's enforced here
// rather than trusted from the response.
const STAR_ORDER = ['situation', 'task', 'action', 'result'];

// -> [{ key, label, score100, note, feedback }], always Situation -> Task -> Action -> Result.
export function starComponentRows(starBreakdown) {
  const comps = starBreakdown?.components;
  if (!comps || typeof comps !== 'object') return [];
  return Object.entries(comps)
    .map(([key, val]) => ({
      key,
      label: prettyCategory(key),
      score100: Number.isFinite(val?.category_score) ? Math.round(val.category_score) : null,
      note: val?.note || '',
      feedback: val?.feedback || '',
    }))
    .sort((a, b) => {
      const ai = STAR_ORDER.indexOf(a.key);
      const bi = STAR_ORDER.indexOf(b.key);
      return (ai === -1 ? STAR_ORDER.length : ai) - (bi === -1 ? STAR_ORDER.length : bi);
    });
}

// Backend score may be /10 or /100 — normalize to /100.
export function normalizeResponseScore(raw) {
  if (!Number.isFinite(raw)) return null;
  return raw <= 10 ? Math.round(raw * 10) : Math.round(raw);
}

export const HISTORY_STATUS_CONFIG = {
  done: {
    label: 'Done',
    icon: CheckCircle,
    badgeClass: (isDarkMode) => isDarkMode
      ? 'border-primary-400/40 bg-primary-400/10 text-primary-400'
      : 'border-primary-500/50 bg-primary-500/10 text-primary-600',
  },
  transcribing: {
    label: 'Transcribing',
    icon: Loader2,
    iconClass: 'animate-spin',
    badgeClass: (isDarkMode) => isDarkMode
      ? 'border-indigo-400/40 bg-indigo-400/10 text-indigo-400'
      : 'border-indigo-500/50 bg-indigo-500/10 text-indigo-600',
  },
  loading: {
    label: 'Analyzing',
    icon: Loader2,
    iconClass: 'animate-spin',
    badgeClass: (isDarkMode) => isDarkMode
      ? 'border-blue-400/40 bg-blue-400/10 text-blue-400'
      : 'border-blue-500/50 bg-blue-500/10 text-blue-600',
  },
  error: {
    label: 'Failed',
    icon: XCircle,
    badgeClass: (isDarkMode) => isDarkMode
      ? 'border-red-400/40 bg-red-400/10 text-red-400'
      : 'border-red-500/50 bg-red-500/10 text-red-600',
  },
  incomplete: {
    label: 'Incomplete',
    icon: Clock,
    badgeClass: (isDarkMode) => isDarkMode
      ? 'border-yellow-400/40 bg-yellow-400/10 text-yellow-400'
      : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-600',
  },
};
